import { useState, useRef, useCallback, useEffect } from 'react';
import { AmbientSound, AmbientPlayerState, UseAmbientPlayerResult } from '@/types';
import { AMBIENT_CONFIG } from '@/config/ambient';

export const useAmbientPlayer = (): UseAmbientPlayerResult => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const currentSoundRef = useRef<AmbientSound | null>(null);

  const [state, setState] = useState<AmbientPlayerState>(() => {
    // 从localStorage恢复状态
    const saved = localStorage.getItem(AMBIENT_CONFIG.storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          isPlaying: false, // 不自动播放
          currentSound: parsed.currentSound || null,
          volume: parsed.volume || AMBIENT_CONFIG.defaultVolume,
          isExpanded: false
        };
      } catch {
        // 忽略解析错误，使用默认值
      }
    }
    return {
      isPlaying: false,
      currentSound: null,
      volume: AMBIENT_CONFIG.defaultVolume,
      isExpanded: false
    };
  });

  // 保存状态到localStorage
  const saveState = useCallback((newState: Partial<AmbientPlayerState>) => {
    const stateToSave = { ...state, ...newState };
    localStorage.setItem(AMBIENT_CONFIG.storageKey, JSON.stringify({
      currentSound: stateToSave.currentSound,
      volume: stateToSave.volume
    }));
  }, [state]);


  // 播放声音
  const play = useCallback(async (sound: AmbientSound) => {
    try {
      // 防止重复点击造成的竞态条件
      if (isPlayingRef.current && currentSoundRef.current?.id === sound.id) {
        console.log('已经在播放相同音频，忽略重复请求');
        return;
      }

      console.log('准备播放音频:', sound.name);

      // 先停止当前播放（如果有）
      if (audioRef.current) {
        console.log('停止当前音频');
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.removeEventListener('error', () => {});
        audioRef.current.removeEventListener('loadstart', () => {});
        audioRef.current.removeEventListener('canplay', () => {});
        audioRef.current.removeEventListener('ended', () => {});
        audioRef.current = null;
      }

      // 重置状态引用
      isPlayingRef.current = false;
      currentSoundRef.current = null;

      // 等待一个microtask确保清理完成
      await new Promise(resolve => setTimeout(resolve, 0));

      console.log('创建新音频对象:', sound.url);
      
      // 创建新的音频对象
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.loop = true;
      audio.volume = state.volume;
      
      // 设置音频事件监听
      const errorHandler = (e: Event) => {
        console.error('音频加载失败:', e);
        console.error('音频URL:', sound.url);
        console.error('音频错误详情:', audio.error);
        isPlayingRef.current = false;
        currentSoundRef.current = null;
        setState(prev => ({ ...prev, isPlaying: false }));
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
      };

      const loadstartHandler = () => {
        console.log('开始加载音频:', sound.name);
      };

      const canplayHandler = () => {
        console.log('音频可以开始播放:', sound.name);
      };

      const endedHandler = () => {
        console.log('音频播放结束:', sound.name);
        // 循环播放应该不会触发这个事件
      };

      audio.addEventListener('error', errorHandler);
      audio.addEventListener('loadstart', loadstartHandler);
      audio.addEventListener('canplay', canplayHandler);
      audio.addEventListener('ended', endedHandler);

      // 设置引用和状态
      audioRef.current = audio;
      currentSoundRef.current = sound;
      
      // 先设置UI状态，让用户立即看到反馈
      setState(prev => ({ ...prev, isPlaying: true, currentSound: sound }));
      
      // 设置音频源并开始播放
      audio.src = sound.url;
      
      console.log('开始播放音频...');
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('音频播放成功:', sound.name);
        
        // 播放成功后更新引用状态
        isPlayingRef.current = true;
        
        // 保存状态到localStorage
        const newState = {
          isPlaying: true,
          currentSound: sound
        };
        saveState(newState);
      }

    } catch (error) {
      console.error('播放失败:', error);
      
      // 重置所有状态
      isPlayingRef.current = false;
      currentSoundRef.current = null;
      setState(prev => ({ ...prev, isPlaying: false }));
      
      // 清理失败的音频对象
      if (audioRef.current) {
        audioRef.current = null;
      }
    }
  }, [state.volume, saveState]);

  // 停止播放
  const stop = useCallback(() => {
    console.log('停止音频播放');
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    // 重置引用状态
    isPlayingRef.current = false;
    currentSoundRef.current = null;

    const newState = { isPlaying: false };
    setState(prev => ({ ...prev, ...newState }));
    saveState(newState);
  }, [saveState]);

  // 设置音量
  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    
    if (audioRef.current && isPlayingRef.current) {
      audioRef.current.volume = clampedVolume;
    }

    const newState = { volume: clampedVolume };
    setState(prev => ({ ...prev, ...newState }));
    saveState(newState);
  }, [saveState]);

  // 切换展开状态
  const toggleExpanded = useCallback(() => {
    setState(prev => ({ ...prev, isExpanded: !prev.isExpanded }));
  }, []);

  // 清理资源
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return {
    state,
    actions: {
      play,
      stop,
      setVolume,
      toggleExpanded
    }
  };
};